import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

type DividerStyle = "ornamental" | "minimal" | "floral" | "gothic";
type DividerVariant = "primary" | "burgundy" | "gradient";

interface SectionDividerProps {
  style?: DividerStyle;
  variant?: DividerVariant;
  className?: string;
}

export const SectionDivider = ({ 
  style = "ornamental", 
  variant = "gradient",
  className 
}: SectionDividerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const dividerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (dividerRef.current) {
      observer.observe(dividerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const getColors = () => {
    switch (variant) {
      case "primary":
        return { stroke: "hsl(var(--primary))", glow: "hsl(var(--primary))" };
      case "burgundy":
        return { stroke: "hsl(var(--burgundy))", glow: "hsl(var(--burgundy))" };
      case "gradient":
        return { stroke: "url(#divider-gradient)", glow: "hsl(var(--primary))" };
    }
  };

  const colors = getColors();

  const renderOrnamental = () => (
    <svg
      viewBox="0 0 800 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <defs>
        <linearGradient id="divider-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
          <stop offset="50%" stopColor="hsl(var(--burgundy))" stopOpacity="0.8" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Central ornament */}
      <circle cx="400" cy="30" r="8" fill={colors.stroke} filter="url(#glow)" className="animate-pulse" style={{ animationDuration: "3s" }} />
      
      {/* Left curve */}
      <path
        d="M 50 30 Q 150 10, 250 30 Q 300 40, 350 30"
        stroke={colors.stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        className={cn(
          "transition-all duration-1000",
          isVisible ? "opacity-100" : "opacity-0"
        )}
        style={{
          strokeDasharray: 400,
          strokeDashoffset: isVisible ? 0 : 400,
          transition: "stroke-dashoffset 2s ease-out"
        }}
      />
      
      {/* Right curve */}
      <path
        d="M 450 30 Q 500 40, 550 30 Q 650 10, 750 30"
        stroke={colors.stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        className={cn(
          "transition-all duration-1000",
          isVisible ? "opacity-100" : "opacity-0"
        )}
        style={{
          strokeDasharray: 400,
          strokeDashoffset: isVisible ? 0 : 400,
          transition: "stroke-dashoffset 2s ease-out 0.2s"
        }}
      />
      
      {/* Decorative dots */}
      <circle cx="250" cy="30" r="3" fill={colors.stroke} opacity="0.6" />
      <circle cx="550" cy="30" r="3" fill={colors.stroke} opacity="0.6" />
    </svg>
  );

  const renderMinimal = () => (
    <svg
      viewBox="0 0 800 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <defs>
        <linearGradient id="divider-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.2" />
          <stop offset="50%" stopColor="hsl(var(--burgundy))" stopOpacity="0.6" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.2" />
        </linearGradient>
      </defs>
      
      <line x1="0" y1="20" x2="350" y2="20" stroke={colors.stroke} strokeWidth="1" opacity="0.5" />
      <line x1="450" y1="20" x2="800" y2="20" stroke={colors.stroke} strokeWidth="1" opacity="0.5" />
      
      {/* Central diamond */}
      <path
        d="M 400 10 L 410 20 L 400 30 L 390 20 Z"
        stroke={colors.stroke}
        strokeWidth="1.5"
        fill="none"
        className={cn(
          "transition-all duration-500",
          isVisible ? "opacity-100 scale-100" : "opacity-0 scale-50"
        )}
        style={{ transformOrigin: "400px 20px" }}
      />
    </svg>
  );

  const renderFloral = () => (
    <svg
      viewBox="0 0 800 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <defs>
        <linearGradient id="divider-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
          <stop offset="50%" stopColor="hsl(var(--burgundy))" stopOpacity="0.8" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      
      {/* Central flower */}
      <circle cx="400" cy="40" r="6" fill={colors.stroke} opacity="0.8" />
      
      {/* Petals */}
      <ellipse cx="400" cy="25" rx="4" ry="8" fill={colors.stroke} opacity="0.5" />
      <ellipse cx="400" cy="55" rx="4" ry="8" fill={colors.stroke} opacity="0.5" />
      <ellipse cx="385" cy="40" rx="8" ry="4" fill={colors.stroke} opacity="0.5" />
      <ellipse cx="415" cy="40" rx="8" ry="4" fill={colors.stroke} opacity="0.5" />
      
      {/* Vines */}
      <path
        d="M 100 40 Q 200 20, 300 40 Q 350 50, 380 40"
        stroke={colors.stroke}
        strokeWidth="1"
        opacity="0.4"
        strokeLinecap="round"
      />
      <path
        d="M 420 40 Q 450 50, 500 40 Q 600 20, 700 40"
        stroke={colors.stroke}
        strokeWidth="1"
        opacity="0.4"
        strokeLinecap="round"
      />
      
      {/* Small leaves */}
      <ellipse cx="200" cy="35" rx="3" ry="5" fill={colors.stroke} opacity="0.3" transform="rotate(30 200 35)" />
      <ellipse cx="600" cy="35" rx="3" ry="5" fill={colors.stroke} opacity="0.3" transform="rotate(-30 600 35)" />
    </svg>
  );

  const renderGothic = () => (
    <svg
      viewBox="0 0 800 70"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <defs>
        <linearGradient id="divider-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
          <stop offset="50%" stopColor="hsl(var(--burgundy))" stopOpacity="0.9" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      
      {/* Central arch */}
      <path
        d="M 380 35 L 400 10 L 420 35"
        stroke={colors.stroke}
        strokeWidth="2"
        fill="none"
        strokeLinejoin="miter"
      />
      
      {/* Side arches */}
      <path
        d="M 200 35 L 220 20 L 240 35"
        stroke={colors.stroke}
        strokeWidth="1.5"
        fill="none"
        opacity="0.6"
      />
      <path
        d="M 560 35 L 580 20 L 600 35"
        stroke={colors.stroke}
        strokeWidth="1.5"
        fill="none"
        opacity="0.6"
      />
      
      {/* Connecting lines */}
      <line x1="50" y1="35" x2="200" y2="35" stroke={colors.stroke} strokeWidth="1" opacity="0.4" />
      <line x1="240" y1="35" x2="380" y2="35" stroke={colors.stroke} strokeWidth="1" opacity="0.4" />
      <line x1="420" y1="35" x2="560" y2="35" stroke={colors.stroke} strokeWidth="1" opacity="0.4" />
      <line x1="600" y1="35" x2="750" y2="35" stroke={colors.stroke} strokeWidth="1" opacity="0.4" />
      
      {/* Vertical accents */}
      <line x1="400" y1="10" x2="400" y2="5" stroke={colors.stroke} strokeWidth="2" opacity="0.8" />
    </svg>
  );

  const renderDivider = () => {
    switch (style) {
      case "ornamental":
        return renderOrnamental();
      case "minimal":
        return renderMinimal();
      case "floral":
        return renderFloral();
      case "gothic":
        return renderGothic();
      default:
        return renderOrnamental();
    }
  };

  return (
    <div 
      ref={dividerRef}
      className={cn(
        "w-full max-w-4xl mx-auto my-20 px-4 transition-opacity duration-1000",
        isVisible ? "opacity-100" : "opacity-0",
        className
      )}
      style={{
        filter: `drop-shadow(0 0 8px ${colors.glow}30)`
      }}
    >
      <div className="relative">
        {renderDivider()}
      </div>
    </div>
  );
};
